import { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus, Save, RotateCcw, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SKILL_RE = /^[A-Za-z0-9 \-/À-ÿĂÂÎȘȚăâîșț.+#&]{1,30}$/;

export default function SkillsEditor({ employee, allSkills, onSaved, onCancel }) {
  const [skills, setSkills] = useState(() => Array.isArray(employee.skills) ? [...employee.skills] : []);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setSkills(Array.isArray(employee.skills) ? [...employee.skills] : []);
  }, [employee.id, employee.skills]);

  const suggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return [];
    const have = new Set(skills.map((s) => s.toLowerCase()));
    return allSkills
      .filter((s) => s.name.toLowerCase().includes(q) && !have.has(s.name.toLowerCase()))
      .slice(0, 6);
  }, [input, allSkills, skills]);

  const exactExists = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q) return false;
    return allSkills.some((s) => s.name.toLowerCase() === q) || skills.some((s) => s.toLowerCase() === q);
  }, [input, allSkills, skills]);

  const addSkill = (raw) => {
    const v = raw.trim().replace(/\s+/g, " ");
    if (!v) return;
    if (skills.length >= 10) {
      toast.error("Maxim 10 competențe");
      return;
    }
    if (!SKILL_RE.test(v)) {
      toast.error("Caractere nepermise sau prea lung (max 30)");
      return;
    }
    if (skills.some((s) => s.toLowerCase() === v.toLowerCase())) {
      setInput("");
      return;
    }
    setSkills([...skills, v]);
    setInput("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeSkill = (s) => setSkills(skills.filter((x) => x !== s));

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestions[0]) {
        addSkill(suggestions[0].name);
      } else if (input.trim()) {
        addSkill(input);
      }
    } else if (e.key === "Backspace" && !input && skills.length) {
      setSkills(skills.slice(0, -1));
    }
  };

  const save = async () => {
    // Flush any pending typed-but-not-confirmed skill so it isn't lost
    let finalSkills = skills;
    const pending = input.trim().replace(/\s+/g, " ");
    if (pending) {
      if (!SKILL_RE.test(pending)) {
        toast.error("Caractere nepermise sau prea lung (max 30)");
        return;
      }
      const already = finalSkills.some((s) => s.toLowerCase() === pending.toLowerCase());
      if (!already) {
        if (finalSkills.length >= 10) {
          toast.error("Maxim 10 competențe");
          return;
        }
        finalSkills = [...finalSkills, pending];
      }
    }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/employees/${employee.id}/skills`, { skills: finalSkills });
      setSkills(finalSkills);
      setInput("");
      toast.success("Competențe salvate");
      onSaved?.(r.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Eroare la salvare";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="skills-editor" className="space-y-3">
      {/* Current skills */}
      <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-2 rounded-lg border border-border bg-zinc-50 dark:bg-zinc-800/40">
        {skills.length === 0 && (
          <span className="text-xs text-zinc-400 self-center px-1">Niciuna încă. Adaugă mai jos.</span>
        )}
        {skills.map((s) => (
          <span
            key={s}
            data-testid={`skill-pill-edit-${s}`}
            className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 text-xs font-medium rounded-full bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
          >
            {s}
            <button
              data-testid={`skill-remove-${s}`}
              type="button"
              onClick={() => removeSkill(s)}
              className="ml-0.5 inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-white/20 dark:hover:bg-black/10"
              aria-label={`Elimină ${s}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>

      {/* Add input */}
      <div className="relative">
        <input
          ref={inputRef}
          data-testid="skill-input"
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          onKeyDown={onKeyDown}
          placeholder="Adaugă o competență (Enter pentru a confirma)"
          className="w-full h-10 px-3 pr-9 rounded-lg border border-border bg-white dark:bg-zinc-950 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-100 transition-shadow duration-150"
        />
        <button
          type="button"
          data-testid="skill-add-btn"
          onClick={() => input.trim() && addSkill(input)}
          disabled={!input.trim()}
          className="absolute right-1.5 top-1.5 h-7 w-7 inline-flex items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
        </button>

        {showSuggestions && input.trim() && (
          <div data-testid="skill-suggestions" className="absolute z-10 mt-1 w-full bg-white dark:bg-zinc-900 border border-border rounded-lg shadow-lg overflow-hidden">
            {suggestions.map((s) => (
              <button
                key={s.name}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addSkill(s.name)}
                data-testid={`skill-suggestion-${s.name}`}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
              >
                <span>{s.name}</span>
                <span className="text-[10px] text-zinc-400">{s.count}</span>
              </button>
            ))}
            {!exactExists && input.trim() && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addSkill(input)}
                data-testid="skill-create-new"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors border-t border-border"
              >
                <Sparkles className="h-3.5 w-3.5 text-emerald-500" />
                <span>Adaugă <span className="font-semibold">"{input.trim()}"</span></span>
              </button>
            )}
            {suggestions.length === 0 && exactExists && (
              <div className="px-3 py-2 text-xs text-zinc-400">Deja adăugat.</div>
            )}
          </div>
        )}
      </div>

      <div className="text-[11px] text-zinc-500 dark:text-zinc-400">{skills.length}/10 competențe.</div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          data-testid="skills-save-btn"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 h-9 rounded-lg bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <Save className="h-4 w-4" /> {saving ? "Se salvează..." : "Salvează"}
        </button>
        <button
          data-testid="skills-cancel-btn"
          onClick={onCancel}
          className="inline-flex items-center gap-2 px-4 h-9 rounded-lg border border-border text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
        >
          <RotateCcw className="h-4 w-4" /> Anulează
        </button>
      </div>
    </div>
  );
}
